import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { createPortal } from 'react-dom';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  width?: number | string;
  footer?: React.ReactNode;
}

export function Modal({ open, onClose, title, children, width = 480, footer }: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      ref={overlayRef}
      onClick={e => { if (e.target === overlayRef.current) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0, 0, 0, 0.55)',
        backdropFilter: 'blur(var(--lg-blur)) saturate(var(--lg-saturation))',
        WebkitBackdropFilter: 'blur(var(--lg-blur)) saturate(var(--lg-saturation))',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          background: 'var(--glass-bg-elevated)',
          backdropFilter: 'blur(var(--lg-blur)) saturate(var(--lg-saturation))',
          WebkitBackdropFilter: 'blur(var(--lg-blur)) saturate(var(--lg-saturation))',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          borderRadius: 'var(--radius-container-lg)',
          width: '100%',
          maxWidth: width,
          maxHeight: '90dvh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 64px rgba(0, 0, 0, 0.5), 0 1px 0 rgba(255,255,255,0.08) inset',
          animation: 'modal-spring-in 280ms cubic-bezier(0.16,1,0.3,1)',
          willChange: 'transform',
        }}
      >
        <style>{`
          @keyframes modal-spring-in {
            0%   { opacity: 0; transform: scale(0.88) translateY(16px); }
            50%  { opacity: 1; transform: scale(1.015) translateY(-2px); }
            100% { opacity: 1; transform: scale(1) translateY(0); }
          }
        `}</style>

        {title && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '1rem 1.25rem',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          }}>
            <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600, color: 'var(--color-text)' }}>{title}</h3>
            <button
              onClick={onClose}
              aria-label="Chiudi"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--color-text-muted)', padding: '0.25rem',
                borderRadius: 'var(--radius-interactive)', display: 'flex',
                alignItems: 'center',
                transition: 'color var(--spring-duration) var(--spring-easing)',
              }}
              className="pressable"
            >
              <X size={18} />
            </button>
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto', padding: '1.25rem' }}>
          {children}
        </div>

        {footer && (
          <div style={{
            padding: '1rem 1.25rem',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex', gap: '0.5rem', justifyContent: 'flex-end',
          }}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: React.ReactNode;
  confirmLabel?: string;
  loading?: boolean;
  /** If true, user must check a checkbox before the confirm button is enabled */
  requiresConfirmCheck?: boolean;
}

export function ConfirmDialog({
  open, onClose, onConfirm, title, description,
  confirmLabel = 'Elimina', loading, requiresConfirmCheck,
}: ConfirmDialogProps) {
  const [checked, setChecked] = React.useState(false);
  const canConfirm = !requiresConfirmCheck || checked;

  // Reset checkbox when closed
  React.useEffect(() => {
    if (!open) setChecked(false);
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      width={420}
      footer={
        <>
          <button
            onClick={onClose}
            style={{
              background: 'var(--glass-bg-card)',
              backdropFilter: 'blur(8px) saturate(180%)',
              WebkitBackdropFilter: 'blur(8px) saturate(180%)',
              border: 'var(--glass-border)',
              borderRadius: 'var(--radius-interactive)',
              color: 'var(--color-text)',
              padding: '0.5rem 1.25rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all var(--spring-duration) var(--spring-easing)',
            }}
          >
            Annulla
          </button>
          <button
            onClick={onConfirm}
            disabled={loading || !canConfirm}
            style={{
              background: 'rgba(239, 68, 68, 0.10)',
              border: '0.5px solid rgba(239, 68, 68, 0.25)',
              borderRadius: 'var(--radius-interactive)',
              color: 'var(--color-error)',
              padding: '0.5rem 1.25rem',
              fontWeight: 600,
              cursor: canConfirm ? 'pointer' : 'not-allowed',
              opacity: loading || !canConfirm ? 0.4 : 1,
              transition: 'all var(--spring-duration) var(--spring-easing)',
            }}
          >
            {loading ? '...' : confirmLabel}
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', lineHeight: 1.6 }}>{description}</p>
        {requiresConfirmCheck && (
          <label style={{
            display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
            fontSize: 'var(--text-sm)', cursor: 'pointer', userSelect: 'none',
            color: 'var(--color-text)',
          }}>
            <input
              type="checkbox"
              checked={checked}
              onChange={e => setChecked(e.target.checked)}
              style={{ marginTop: '0.125rem', accentColor: 'var(--color-error)', flexShrink: 0 }}
            />
            Confermo di voler procedere con questa operazione
          </label>
        )}
      </div>
    </Modal>
  );
}
